import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { auth } from '../../firebase';
import ConfirmationModal from './ConfirmationModal';
import { API_URL, SOCKET_URL } from '../../apiConfig';

const PartnersPage = ({ user, setUser }) => { // Accept user and setUser as props
    const [rewards, setRewards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // --- NEW: State for the confirmation modal ---
    const [modalState, setModalState] = useState({
        isOpen: false,
        status: 'confirm', // 'confirm', 'success', 'error'
        reward: null,
    });

    useEffect(() => {
        const fetchRewards = async () => {
            try {
                const response = await axios.get(`${API_URL}/rewards`);
                setRewards(response.data);
            } catch (err) {
                console.error("Error fetching rewards:", err);
                setError("Could not load partner offers at this time.");
            } finally {
                setLoading(false);
            }
        };

        fetchRewards();
    }, []);

    const handleRedeemClick = (reward) => {
        // Check if user has enough points before even showing the confirmation
        if (user.points < reward.pointsCost) {
            setModalState({ isOpen: true, status: 'error', reward: reward });
        } else {
            setModalState({ isOpen: true, status: 'confirm', reward: reward });
        }
    };
    
    const confirmRedemption = async () => {
        const { reward } = modalState;
        if (!reward) return;

        try {
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) throw new Error("You must be logged in to redeem rewards.");
            const idToken = await firebaseUser.getIdToken();

            const response = await axios.post(
                `${API_URL}/rewards/${reward._id}/redeem`,
                {},
                { headers: { Authorization: `Bearer ${idToken}` } }
            );
            
            // Update user points in the parent state
            setUser(prevUser => ({ ...prevUser, points: response.data.newPointsTotal }));
            
            // Show the success modal
            setModalState({ isOpen: true, status: 'success', reward: reward });

        } catch (err) {
            console.error("Error redeeming reward:", err);
            alert(err.response?.data?.error || "Failed to redeem reward.");
            closeModal();
        }
    };

    const closeModal = () => {
        setModalState({ isOpen: false, status: 'confirm', reward: null });
    };

    return (
        <>
            <ConfirmationModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                onConfirm={confirmRedemption}
                status={modalState.status}
                reward={modalState.reward}
                userPoints={user?.points}
            />
            <motion.div
                key="partners"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-4 md:p-8 max-w-5xl mx-auto"
            >
                <div className="mb-8 flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                            Partner Offers
                        </h1>
                        <p className="text-gray-600 mt-1">Redeem your points for exclusive rewards.</p>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-500">Your Points</p>
                        <p className="text-2xl font-bold text-black">{user?.points || 0}</p>
                    </div>
                </div>
                
                {loading ? (
                    <p>Loading offers...</p>
                ) : error ? (
                    <p className="text-red-500">{error}</p>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {rewards.map((reward, index) => (
                            <motion.div 
                                key={reward._id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="bg-white rounded-xl shadow-md border border-gray-200 flex flex-col"
                            >
                                <div className="p-5 flex-grow">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="text-lg font-bold text-gray-800">{reward.partner.name}</h3>
                                        <span className="bg-gray-100 text-gray-800 px-2.5 py-0.5 rounded-full text-xs font-medium">{reward.partner.category}</span>
                                    </div>
                                    <p className="text-2xl font-bold text-gray-900 mb-4">{reward.description}</p>
                                    <p className="text-sm text-gray-500">
                                        Cost: <span className="font-semibold text-gray-700">{reward.pointsCost} points</span>
                                    </p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-b-xl">
                                    <button 
                                        onClick={() => handleRedeemClick(reward)}
                                        className="w-full py-2 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all text-sm"
                                    >
                                        Redeem Offer
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
                {!loading && rewards.length === 0 && (
                    <div className="text-center py-10 bg-white rounded-xl shadow-md border border-gray-200">
                        <p className="text-gray-500">There are no partner offers available at this time.</p>
                    </div>
                )}
            </motion.div>
        </>
    );
};

export default PartnersPage;
